import type { Ring } from '../../../../geo/types';
import type { LinearRGB } from '../../../../core/color';
import type { Landmark } from '../../../../data/types';
import type { MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { SURFACE, type SurfaceKind } from '../../../mesh/attributes';
import type { DetailSink } from '../../detailSink';
import { transverseRoof } from '../gothic/roofs';
import { edgePanel, openedWall, type MasonryStyles } from '../masonry';
import { meshFrameOf } from '../planFrame';
import { stadhuisPlanOf } from './stadhuisPlan';
import { crossWindows, windowedWall, type StadhuisBuild } from './stadhuisParts';
import { buildVierschaar } from './stadhuisVierschaar';
import { buildStadhuisTower } from './stadhuisTower';
import { buildStadhuisHall } from './stadhuisHall';
import { LANDMARK_PATTERN, patterned } from '../surfacePatterns';
import { PATTERN } from '../../../../render/patterns/patternIds';

/**
 * The share of the tower exaggeration the painter gave the town hall, fitted to its traced
 * silhouette (docs/plans/landmarks.md, "Silhouettes"), and its part of the landmark triangle budget.
 */
export const STADHUIS_STYLE = { share: 0.61, maxTriangles: 10_000 } as const;

export interface StadhuisColors {
  readonly stone: LinearRGB;
  readonly brick: LinearRGB;
  readonly slate: LinearRGB;
  readonly dark: LinearRGB;
  readonly glass: LinearRGB;
  readonly leaf: LinearRGB;
}

export interface StadhuisContext {
  readonly squares: readonly Ring[];
  readonly details: DetailSink;
}

interface Ids {
  readonly edgeId: number;
  readonly seed: number;
}

function stylesOf(colors: StadhuisColors, ids: Ids) {
  const base = {
    ...ids,
    strokeAngle: 0,
    extent: [10, 10] as const,
    exaggeration: { base: 0, share: STADHUIS_STYLE.share },
  };
  const style = (color: LinearRGB, surface: SurfaceKind): VertexStyle => ({
    ...base,
    color,
    surface,
  });
  const dark = style(colors.dark, SURFACE.linework);
  const glass = style(colors.glass, SURFACE.linework);
  const roof = patterned(style(colors.slate, SURFACE.roofSlate), LANDMARK_PATTERN.slate);
  // Civic windows sit in their masonry. The 25 cm reveal is a reconstruction estimate;
  // keeping their glass on the wall plane loses the jambs and sills in a walking view.
  const masonry = (wall: VertexStyle): MasonryStyles => ({
    wall,
    roof,
    opening: glass,
    glazing: { reveal: wall },
  });
  return {
    stone: masonry(style(colors.stone, SURFACE.stone)),
    // Reuse the period brick module. The hall's precise bond is not known from the image.
    brick: masonry(patterned(style(colors.brick, SURFACE.brick), PATTERN.houseBrick1538)),
    dark: { wall: dark, roof: dark, opening: dark },
    glass,
    leaf: style(colors.leaf, SURFACE.wood),
  };
}

/** Lower separate rear ranges: their hidden arrangement and openings are reconstruction estimates. */
function buildBackRanges(s: StadhuisBuild): void {
  for (const r of s.plan.backRanges) {
    const { u0, u1, v0, v1, eaveY, ridgeY } = r;
    const ring = [
      [u0, v0],
      [u1, v0],
      [u1, v1],
      [u0, v1],
    ] as const;
    ring.forEach((from, i) => {
      const to = ring[(i + 1) % 4]!;
      const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
      const outline = [
        [0, 0],
        [length, 0],
        [length, eaveY],
        [0, eaveY],
      ] as const;
      const { vierschaar } = s.plan;
      // This hidden range has a 60 cm service gap against the court. Its close-facing wall is
      // plain masonry; windows on the exposed rear elevations remain reconstruction estimates.
      const courtFacing =
        i === 3 &&
        u0 > vierschaar.u1 &&
        u0 - vierschaar.u1 < 1 &&
        v0 < vierschaar.v1 &&
        v1 > vierschaar.v0;
      const windows = courtFacing
        ? []
        : crossWindows(length, 4.5, [{ sill: 2.6, top: 4.6, width: 0.8 }]);
      windowedWall(s, s.brick, [from, to], outline, windows);
    });
    /* Gable triangles are emitted as walls rather than overlapping a second rectangle. */
    const front = { wall: s.brick.wall, roof: s.brick.roof, opening: s.brick.wall };
    // The vertical ends above the eaves have no recorded openings.
    const triangle = [
      [0, eaveY],
      [u1 - u0, eaveY],
      [(u1 - u0) / 2, ridgeY],
    ] as const;
    openedWall(s.builder, front, s.frame, edgePanel([u0, v0], [u1, v0]), triangle, []);
    openedWall(s.builder, front, s.frame, edgePanel([u1, v1], [u0, v1]), triangle, []);
    transverseRoof(s.builder, s.brick, s.frame, r, { eaveY, ridgeY });
  }
}

/** The town hall on the Dam in 1538; returns the triangles it added. */
export function buildStadhuis(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: StadhuisColors,
  context: StadhuisContext,
  ids: Ids
): number {
  const plan = stadhuisPlanOf(landmark);
  const frame = meshFrameOf(plan.frame.plan);
  const styles = stylesOf(colors, ids);
  const facade = {
    builder,
    frame,
    details: context.details,
    edgeId: ids.edgeId,
    styles: { wall: styles.brick.wall, glass: styles.glass, leaf: styles.leaf },
  };
  const build: StadhuisBuild = {
    builder,
    frame,
    plan,
    stone: styles.stone,
    brick: styles.brick,
    dark: styles.dark,
    glass: styles.glass,
    facade,
  };
  const before = builder.triangleCount;
  buildVierschaar(build);
  buildStadhuisTower(build);
  buildStadhuisHall(build);
  buildBackRanges(build);
  return builder.triangleCount - before;
}
